/**
 * Studio Booking · Amenities editors — extracted Oct 2026.
 * Owns the curated icon list + the three editors used by Studios /
 * Spaces configuration:
 *   • AmenityIcon      — single lucide icon by key (shared read-side)
 *   • AmenityIconPicker — popover grid picker + search
 *   • AmenityListEditor — add/remove rows (name + icon + description)
 *   • SpaceAmenitiesEditor — inherit/override/extend wrapper + preview
 *
 * No cross-component state; the only external contract is the string
 * `icon` key persisted on each amenity row — the public booking widget
 * (`StudioBookingWidget.jsx`) maps the same keys to the same lucide
 * components, so adding/removing icons here MUST be mirrored there.
 */
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Wifi, Car, ParkingCircle, Coffee, Utensils, Snowflake, Fan, Tv, Music,
  Speaker, Mic, Wrench, Sofa, Shirt, ShowerHead, Toilet, Sparkles, DoorOpen,
  Baby, PawPrint, Accessibility, Zap, Battery, Lightbulb, Cctv, Lock,
  ShieldCheck, Wine, Refrigerator, Armchair, Bath, Bed,
} from "lucide-react";

// Curated amenity icons — key stays in Mongo (`icon` field), the
// component maps back to the lucide component at render-time. The
// list is intentionally short + studio-relevant so admins never have
// to hunt through 1000+ icons. Extend here as needed; every icon
// used on the public widget MUST live in this list so the customer
// booking page renders the same lucide shape.
export const AMENITY_ICONS = [
  { key: "wifi",          label: "Wi-Fi",           C: Wifi },
  { key: "parking-circle", label: "Parking",        C: ParkingCircle },
  { key: "car",           label: "Car / valet",     C: Car },
  { key: "coffee",        label: "Coffee",          C: Coffee },
  { key: "utensils",      label: "Food · cafe",     C: Utensils },
  { key: "refrigerator",  label: "Fridge",          C: Refrigerator },
  { key: "wine",          label: "Bar",             C: Wine },
  { key: "snowflake",     label: "AC",              C: Snowflake },
  { key: "fan",           label: "Ventilation",     C: Fan },
  { key: "lightbulb",     label: "Lighting",        C: Lightbulb },
  { key: "zap",           label: "Power / mains",   C: Zap },
  { key: "battery",       label: "Battery backup",  C: Battery },
  { key: "tv",            label: "Screen · TV",     C: Tv },
  { key: "music",         label: "Music",           C: Music },
  { key: "speaker",       label: "Speakers",        C: Speaker },
  { key: "mic",           label: "Mic",             C: Mic },
  { key: "wrench",        label: "Tools",           C: Wrench },
  { key: "sofa",          label: "Lounge",          C: Sofa },
  { key: "armchair",      label: "Seating",         C: Armchair },
  { key: "bed",           label: "Nap · rest area", C: Bed },
  { key: "shirt",         label: "Wardrobe",        C: Shirt },
  { key: "sparkles",      label: "Makeup",          C: Sparkles },
  { key: "door-open",     label: "Green room",      C: DoorOpen },
  { key: "shower-head",   label: "Shower",          C: ShowerHead },
  { key: "bath",          label: "Bath",            C: Bath },
  { key: "toilet",        label: "Restroom",        C: Toilet },
  { key: "baby",          label: "Baby-friendly",   C: Baby },
  { key: "paw-print",     label: "Pet-friendly",    C: PawPrint },
  { key: "accessibility", label: "Accessible",      C: Accessibility },
  { key: "cctv",          label: "CCTV",            C: Cctv },
  { key: "lock",          label: "Secure entry",    C: Lock },
  { key: "shield-check",  label: "Insured",         C: ShieldCheck },
];
export const AMENITY_ICON_BY_KEY = Object.fromEntries(AMENITY_ICONS.map((i) => [i.key, i]));

export function AmenityIcon({ iconKey, className = "w-4 h-4" }) {
  if (!iconKey) return null;
  const entry = AMENITY_ICON_BY_KEY[iconKey];
  if (!entry) return null;
  const C = entry.C;
  return <C className={className} />;
}

/* Compact button that opens a popover with the curated icon grid.
   Kept as a simple <details> to avoid pulling in another popover
   dep — the click-outside-to-close behaviour is native. */
export function AmenityIconPicker({ value, onChange, testid = "amenity-icon" }) {
  const [open, setOpen] = useState(false);
  // Feb 2026 · Live search — matches label OR key so admins with dozens
  // of amenities find "cctv"/"wifi" instantly instead of scanning the
  // 6-column grid. Case-insensitive, trimmed, whitespace-safe.
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  useEffect(() => { if (!open) setQuery(""); }, [open]);
  const entry = value ? AMENITY_ICON_BY_KEY[value] : null;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? AMENITY_ICONS.filter((ic) =>
        ic.label.toLowerCase().includes(q) || ic.key.toLowerCase().includes(q))
    : AMENITY_ICONS;
  return (
    <div ref={rootRef} className="relative">
      <Button type="button" size="sm" variant="outline"
              onClick={() => setOpen((v) => !v)}
              className="rounded-none h-8 text-xs w-full justify-start gap-1.5"
              data-testid={`${testid}-btn`}>
        {entry ? (
          <>
            <AmenityIcon iconKey={value} className="w-3.5 h-3.5" />
            <span className="truncate">{entry.label}</span>
          </>
        ) : (
          <span className="text-muted-foreground">Pick an icon…</span>
        )}
      </Button>
      {open && (
        <div className="absolute z-40 top-full left-0 mt-1 w-80 max-w-[calc(100vw-32px)] bg-background border border-border shadow-lg p-2"
              data-testid={`${testid}-panel`}>
          <div className="flex items-center justify-between mb-2 px-1 gap-2">
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search icons…" autoFocus
                    className="rounded-none h-7 text-xs flex-1"
                    data-testid={`${testid}-search`} />
            {value && (
              <button type="button"
                       onClick={() => { onChange(""); setOpen(false); }}
                       className="text-[10px] uppercase tracking-widest font-semibold text-destructive hover:underline whitespace-nowrap"
                       data-testid={`${testid}-clear`}>
                Clear
              </button>
            )}
          </div>
          {filtered.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic p-4 text-center">
              No icon matches "{query}".
            </div>
          ) : (
            <div className="grid grid-cols-6 gap-1 max-h-[220px] overflow-y-auto">
              {filtered.map((ic) => {
                const active = ic.key === value;
                const C = ic.C;
                return (
                  <button key={ic.key} type="button"
                           onClick={() => { onChange(ic.key); setOpen(false); }}
                           title={ic.label}
                           className={`aspect-square flex items-center justify-center border ${active ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"}`}
                           data-testid={`${testid}-opt-${ic.key}`}>
                    <C className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AmenityListEditor({ value, onChange, level = "studio", testid = "amenities" }) {
  const rows = Array.isArray(value) ? value : [];
  const setRows = (r) => onChange(r);
  const patch = (idx, p) =>
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...p } : r)));
  const addRow = () =>
    setRows([...rows, { id: `am-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: "", icon: "", description: "" }]);
  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  return (
    <div data-testid={testid} className="border-t border-border pt-3 mt-2 space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[11px] font-semibold uppercase tracking-widest">
          {level === "studio" ? "Studio amenities" : "Space amenities"}
        </Label>
        <Button type="button" size="sm" variant="outline"
                onClick={addRow} className="rounded-none h-7 text-[11px]"
                data-testid={`${testid}-add`}>
          + Add amenity
        </Button>
      </div>
      <div className="text-[10px] text-muted-foreground -mt-1">
        {level === "studio"
          ? "Base list. Every space under this studio inherits this list by default."
          : "Fine-tune this space's amenities using the mode selector above."}
      </div>
      {rows.length === 0 && (
        <div className="text-[11px] text-muted-foreground italic">
          No amenities yet — click <b>+ Add amenity</b> to add your first one.
        </div>
      )}
      {rows.map((row, idx) => (
        <div key={row.id || `row-${idx}`} className="grid grid-cols-12 gap-2 items-start">
          <div className="col-span-4">
            <Input value={row.name || ""}
                   onChange={(e) => patch(idx, { name: e.target.value })}
                   placeholder="Amenity name (e.g. WiFi)"
                   className="rounded-none h-8 text-xs"
                   data-testid={`${testid}-name-${idx}`} />
          </div>
          <div className="col-span-3">
            <AmenityIconPicker
              value={row.icon || ""}
              onChange={(v) => patch(idx, { icon: v })}
              testid={`${testid}-icon-${idx}`}
            />
          </div>
          <div className="col-span-4">
            <Input value={row.description || ""}
                   onChange={(e) => patch(idx, { description: e.target.value })}
                   placeholder="Short note (optional)"
                   className="rounded-none h-8 text-xs"
                   data-testid={`${testid}-desc-${idx}`} />
          </div>
          <div className="col-span-1 flex justify-end">
            <Button type="button" size="sm" variant="ghost"
                    onClick={() => removeRow(idx)}
                    className="rounded-none h-8 text-xs text-destructive"
                    data-testid={`${testid}-remove-${idx}`}>
              ✕
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}


/* Feb 2026 · Space-amenities inheritance editor. Wraps the raw list
   editor with the `inherit / override / extend` mode selector + a
   live preview of the effective list so the admin sees exactly what
   customers will see on the booking widget without saving first. */
export function SpaceAmenitiesEditor({ value, onChange, studioAmenities = [], testid = "sb-space-amenities" }) {
  const mode = value?.amenities_mode || "inherit";
  const rows = Array.isArray(value?.amenities) ? value.amenities : [];
  const parent = Array.isArray(studioAmenities) ? studioAmenities : [];
  const previewEffective = (() => {
    if (mode === "override") return rows.filter((r) => (r?.name || "").trim());
    if (mode === "extend") {
      const seen = new Set(parent.map((a) => (a?.name || "").trim().toLowerCase()));
      return [
        ...parent.filter((a) => (a?.name || "").trim()),
        ...rows.filter((r) => {
          const n = (r?.name || "").trim();
          return n && !seen.has(n.toLowerCase());
        }),
      ];
    }
    return parent.filter((a) => (a?.name || "").trim());
  })();
  return (
    <div className="border-t border-border pt-3 mt-2 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <Label className="text-[11px] font-semibold uppercase tracking-widest">
          Amenities for this space
        </Label>
        <select value={mode}
                onChange={(e) => onChange({ amenities_mode: e.target.value })}
                className="h-8 border border-border bg-background text-xs rounded-none px-2"
                data-testid={`${testid}-mode`}>
          <option value="inherit">Inherit from studio</option>
          <option value="override">Override (space-only)</option>
          <option value="extend">Extend (studio + space)</option>
        </select>
      </div>
      <div className="text-[10px] text-muted-foreground -mt-1">
        {mode === "inherit" && "Uses the studio's amenity list as-is."}
        {mode === "override" && "Ignores the studio list — this space defines its own."}
        {mode === "extend" && "Studio list + the extras below (duplicates by name dropped)."}
      </div>
      {mode !== "inherit" && (
        <AmenityListEditor
          value={rows}
          onChange={(v) => onChange({ amenities: v })}
          level="space"
          testid={`${testid}-list`}
        />
      )}
      {/* Live preview so the admin never has to save-then-check the
          public widget — same list customers will see in booking. */}
      <div className="mt-2 text-[11px]">
        <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-1">
          Effective (what customers will see)
        </div>
        {previewEffective.length === 0 ? (
          <div className="text-muted-foreground italic">
            No amenities will show on this space's booking page yet.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid={`${testid}-preview`}>
            {previewEffective.map((a, i) => (
              <span key={a.id || `p-${i}`}
                    className="inline-flex items-center gap-1 border border-border px-2 py-0.5 bg-muted/30">
                {a.icon && <AmenityIcon iconKey={a.icon} className="w-3 h-3" />}
                <span>{a.name}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
